import * as React from 'react'
import { Input as InputPrimitive } from '@base-ui/react/input'

import { cn } from '@/lib/utils'
import { HighlightedText } from '@/components/ui/textarea'
import { EyeOff, Eye, Info } from 'lucide-react'

type InputSize = 'md' | 'lg'

interface InputProps extends Omit<React.ComponentProps<'input'>, 'size'> {
  leadingIcon?: React.ReactNode
  trailingIcon?: React.ReactNode
  size?: InputSize
  required?: boolean
  hint?: string
  floatingLabel?: boolean | string | React.ReactElement
  showLimitLabel?: boolean
  highlightBraceTokens?: boolean
}

const EyeToggle = ({ show, onToggle }: { show: boolean; onToggle: () => void }) => (
  <button type="button" onClick={onToggle} className="pointer-events-auto text-[#99A1AF] hover:text-[#1E1E1E]">
    {show ? <EyeOff size={16} /> : <Eye size={16} />}
  </button>
)

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      size = 'md',
      leadingIcon,
      trailingIcon,
      required,
      hint,
      disabled,
      floatingLabel = true,
      showLimitLabel = false,
      highlightBraceTokens = false,
      onScroll,
      ...props
    },
    ref,
  ) => {
    const [overrideType, setOverrideType] = React.useState<React.HTMLInputTypeAttribute | undefined>(type)
    const highlightRef = React.useRef<HTMLDivElement>(null)
    const hasValue = !!props.value
    const rawPlaceholder = props.placeholder as string | undefined
    const floatingLabelText = typeof floatingLabel === 'boolean' ? undefined : floatingLabel
    const hasPersistentFloatingLabel =
      typeof floatingLabelText === 'string' ? !!floatingLabelText.trim() : !!floatingLabelText
    const placeholderHasAsterisk = rawPlaceholder?.endsWith('*') ?? false
    const labelText = floatingLabelText ?? (placeholderHasAsterisk ? rawPlaceholder!.slice(0, -1) : rawPlaceholder)
    const hasWrapper = !!floatingLabel && (!!rawPlaceholder || hasPersistentFloatingLabel)
    const isInvalid = props['aria-invalid'] === true || props['aria-invalid'] === 'true'
    const isFloatingLabelActive = hasValue || hasPersistentFloatingLabel || type === 'datetime-local' || type === 'date'
    const hidePlaceholderText = hasWrapper && !hasPersistentFloatingLabel

    React.useEffect(() => setOverrideType(type), [type])

    if (type === 'password') {
      trailingIcon = (
        <EyeToggle
          show={overrideType === 'text'}
          onToggle={() => setOverrideType(overrideType === 'password' ? 'text' : 'password')}
        />
      )
    }

    const iconWrapperCls = cn(
      'shrink-0 size-6 text-creation-placeholder-icon-focused pointer-events-none flex items-center justify-center',
      size === 'lg' && 'py-1 pr-2',
    )

    const inputEl = (
      <div
        data-size={size}
        data-disabled={disabled ? 'true' : undefined}
        data-invalid={isInvalid ? 'true' : undefined}
        className={cn(
          'flex items-center rounded-300 border border-main-border-lv02 bg-creation-secondary-container-surface-default transition-colors',
          'px-3 data-[size=md]:py-1 data-[size=lg]:py-2',
          'hover:border-creation-primary-container-on-surface-hover',
          'focus-within:border-creation-primary-container-on-surface-focused',
          'data-[disabled=true]:cursor-not-allowed data-[disabled=true]:border-creation-neutral-container-on-surface-disable data-[disabled=true]:bg-creation-secondary-container-surface-disable',
          'data-[invalid=true]:border-creation-error-container-on-surface-default',
        )}
      >
        {leadingIcon && <div className={iconWrapperCls}>{leadingIcon}</div>}
        <div className={highlightBraceTokens ? 'relative flex-1 min-w-0' : 'contents'}>
          {highlightBraceTokens && (
            <div
              ref={highlightRef}
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute inset-0 overflow-hidden whitespace-pre p-1 text-comp-input-ip1 leading-comp-input-ip1',
                disabled ? 'text-creation-placeholder-text-disable-filled' : 'text-creation-placeholder-text-typing',
              )}
            >
              <HighlightedText text={typeof props.value === 'string' ? props.value : ''} />
            </div>
          )}
          <InputPrimitive
            ref={ref}
            type={overrideType}
            data-slot="input"
            disabled={disabled}
            className={cn(
              'flex-1 min-w-0 bg-transparent p-1 outline-none border-0',
              'text-comp-input-ip1 leading-comp-input-ip1',
              'text-creation-placeholder-text-typing',
              'placeholder:text-creation-placeholder-text-default',
              hidePlaceholderText && 'placeholder:text-transparent',
              'file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
              'disabled:cursor-not-allowed disabled:text-creation-placeholder-text-disable-filled disabled:placeholder:text-creation-placeholder-text-disable',
              highlightBraceTokens &&
                'relative w-full text-transparent disabled:text-transparent caret-creation-placeholder-text-typing selection:bg-[Highlight] selection:text-[HighlightText]',
              className,
            )}
            onScroll={(event) => {
              if (highlightRef.current) {
                highlightRef.current.scrollLeft = event.currentTarget.scrollLeft
              }
              onScroll?.(event)
            }}
            {...props}
          />
        </div>
        {trailingIcon && <div className={cn(iconWrapperCls, 'pointer-events-auto')}>{trailingIcon}</div>}
      </div>
    )

    const coreEl = hasWrapper ? (
      <div
        className={cn('group relative rounded-300 overflow-visible', floatingLabel ? 'mt-3' : '')}
        data-active={isFloatingLabelActive || undefined}
        data-invalid={isInvalid ? 'true' : undefined}
      >
        <div
          className={cn(
            'pointer-events-none absolute left-3 z-10 flex gap-0.5 px-1 transition-all duration-200 ease-in-out h-6',
            'flex items-end',
            'top-1/2 -translate-y-1/2 text-[14px] leading-5.5 font-normal',
            'group-focus-within:top-0 group-focus-within:-translate-y-1/2 group-focus-within:text-ui-label-l1 group-focus-within:font-semibold group-focus-within:leading-5',
            'group-data-active:top-0 group-data-active:-translate-y-1/2 group-data-active:text-ui-label-l1 group-data-active:font-semibold group-data-active:leading-ui-label-l1',
            'after:absolute after:inset-0 after:-z-10 after:top-[calc(50%-1px)]',
            disabled
              ? 'after:bg-creation-secondary-container-surface-disable'
              : 'after:bg-creation-secondary-container-surface-default',
          )}
        >
          <span
            className={cn(
              'transition-colors duration-200',
              disabled ? (hasValue ? 'text-main-text-lv01' : 'text-main-text-lv03') : 'text-main-text-lv01',
            )}
          >
            {labelText}
          </span>
          {showLimitLabel && props.maxLength != null && (
            <span className="text-ui-label-l1 leading-ui-label-l1 text-main-text-lv03">
              {`(${props.value ? String(props.value).length : 0}/${props.maxLength})`}
            </span>
          )}
          {(placeholderHasAsterisk || required) && !disabled && (
            <span className="transition-colors duration-200 text-main-text-error">*</span>
          )}
        </div>
        {inputEl}
      </div>
    ) : (
      inputEl
    )

    return (
      <div className={hint ? 'flex flex-col gap-1' : 'contents'}>
        {coreEl}
        {hint && (
          // oxlint-disable-next-line tailwindcss/no-unknown-classes
          <div className="input-hint flex items-center gap-1">
            <Info size={12} className="text-[#9EA8B3]" />
            <span className="text-[12px] leading-5 font-semibold text-[#9EA8B3]">{hint}</span>
          </div>
        )}
      </div>
    )
  },
)
Input.displayName = 'Input'

export { Input }
export type { InputProps }
